import t from "@/lang/lang";
import { TextInput } from "@/shared/components/form/text-input";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { DocumentId, DocumentKind, SubdomainName } from "@bitory-gmbh/bsm";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import {
  CreateRequest,
  useTextblocksTextblockCreateMutation,
} from "../api/textblock-api";

export function CreateTextblockDialog({
  subdomain,
  documentKind,
  documentId,
  disabled,
}: {
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateRequest>({
    id: v4(),
    subdomain,
    documentKind,
    documentId,
    title: "",
    shorthand: "",
    text: "",
  });
  const [patch, { isLoading, error, isSuccess }] =
    useTextblocksTextblockCreateMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest({
        id: v4(),
        subdomain,
        documentKind,
        documentId,
        title: "",
        shorthand: "",
        text: "",
      });
    }
  }, [documentId, documentKind, isSuccess, subdomain]);

  const doCreate = () => {
    if (isLoading || disabled) return;

    patch(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled}>
          <Plus />
          <span>{t("Bemerkung")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bemerkung erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Erstellen Sie eine neue Bemerkung für dieses Dokument.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TextInput
            label={t("Kürzel (optional)")}
            field="shorthand"
            text={request.shorthand}
            onTextChange={(shorthand) => setRequest({ ...request, shorthand })}
            errors={inputValidationErrorMessages(error, "shorthand")}
          />
          <TextInput
            label={t("Titel (optional)")}
            field="title"
            text={request.title}
            onTextChange={(title) => setRequest({ ...request, title })}
            errors={inputValidationErrorMessages(error, "title")}
          />
          <TextareaInput
            label={t("Text")}
            field="text"
            text={request.text}
            onTextChange={(text) => setRequest({ ...request, text })}
            errors={inputValidationErrorMessages(error, "title")}
          />
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={false} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}
